import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FullPasswordSchema } from '../../../utils/schema';
import { SimpleLayout } from '../../layouts/simple-layout';
import { PasswordInput } from '@components/molecules';
import type { ResponseResult } from '@utils';
import { WithControl } from '@utils';

type ChangeTemporaryPasswordFormValues = {
	password: string;
};

export type ChangeTemporaryPasswordFormLabels = {
	title: string;
	subtitle: string;
	submit: string;
	passwordLabel: string;
	repeatPasswordLabel: string;
	passwordDidNotMatch: string;
	passwordErrors: {
		length: string;
		uppercase: string;
		lowercase: string;
		number: string;
	};
};

type Props = {
	labels: ChangeTemporaryPasswordFormLabels;
	onSubmit: (values: ChangeTemporaryPasswordFormValues) => Promise<ResponseResult<any>>;
};

export const ChangeTemporaryPasswordForm: React.FC<Props> = ({ labels, onSubmit }) => {
	const schema = React.useMemo(
		() =>
			z
				.object({
					password: z
						.string()
						.min(8, { message: labels.passwordErrors.length })
						.regex(/[A-Z]+/, { message: labels.passwordErrors.uppercase })
						.regex(/[a-z]+/, { message: labels.passwordErrors.lowercase })
						.regex(/[0-9]+/, { message: labels.passwordErrors.number }),
					repeatPassword: FullPasswordSchema,
				})
				.superRefine((values, ctx) => {
					if (values.password !== values.repeatPassword) {
						return ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: '',
							path: ['repeatPassword'],
						});
					}
				}),
		[]
	);

	const {
		formState: { dirtyFields, isSubmitting, errors, isValid },
		handleSubmit,
		control,
	} = useForm({
		mode: 'onChange',
		defaultValues: {
			password: '',
			repeatPassword: '',
		},
		resolver: zodResolver(schema),
	});

	const ControlledPasswordInput = WithControl(PasswordInput, control);

	return (
		<SimpleLayout
			title={labels.title}
			subtitle={labels.subtitle}
			submitButton={labels.submit}
			handleSubmit={handleSubmit(onSubmit)}
			isValid={dirtyFields.password && dirtyFields.repeatPassword && isValid}
			isLoading={isSubmitting}
		>
			<div className="flex flex-col gap-14">
				<ControlledPasswordInput label={labels.passwordLabel} name="password" error={errors?.password?.message} />
				<ControlledPasswordInput
					label={labels.repeatPasswordLabel}
					name="repeatPassword"
					error={(dirtyFields.repeatPassword && errors.repeatPassword && labels.passwordDidNotMatch) as string}
				/>
			</div>
		</SimpleLayout>
	);
};
